import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { FieldOrderField, RemoteType } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createQueryPreservingReplacingLinkCellFormatter } from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export interface FlowFieldsDisplayGridRowData {
  readonly flowVizportalUrlId: string;
  readonly fieldIcon: FieldIcon | null;
  readonly remoteType?: RemoteType; // Optional remote type to support flow fields that output to columns
  readonly name: string | null;
  readonly typeName: string;
  readonly id: string;
  readonly sheetCount: number | null;
  readonly labels?: ReadonlyArray<GraphLabel>;
}

type FlowFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  FlowFieldsDisplayGridRowData,
  FieldsValidSortFields
>;

function columns(siteUrls: SiteUrls): ReadonlyArray<FlowFieldsColumnFactory> {
  const columns: ReadonlyArray<FlowFieldsColumnFactory> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ fieldIcon, remoteType }) =>
          remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : <SvgFieldIcon fieldIcon={fieldIcon} />,
        ({ fieldIcon, remoteType }) =>
          remoteType ? RemoteTypeMappings[remoteType]() : fieldIcon ? FieldNameMappings[fieldIcon]() : ''
      ),
      sortable: false,
      resizable: false
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: FieldOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
        ({ name }) => name || '',
        (urls, { id, flowVizportalUrlId }) => urls.flowLineageFieldPath(flowVizportalUrlId, id),
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'sheets',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
      sortable: false,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
  ];

  return columns.filter(Boolean) as ReadonlyArray<FlowFieldsColumnFactory>;
}

export type FlowFieldsDisplayGridProps =
  UsualDisplayGridProps<FlowFieldsDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields>;

export function FlowFieldsDisplayGrid(props: FlowFieldsDisplayGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);

  const cols = columns(siteUrls)
    .map(columnFactory => columnFactory(props))
    .filter(column => {
      if ((column.name === 'label-sensitivity' || column.name === 'label-warning') && !customSensitivityLabelsEnabled) {
        return false;
      }
      return true;
    });

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: FlowFieldsDisplayGridRowData): LabelContent {
  const contentParams: LabelContent = {
    contentId: rowData.id,
    contentType: null,
    contentName: rowData.name ?? undefined,
    subjectArea: rowData.typeName === 'FlowFieldOutputField' ? 'FlowFieldOutputField' : 'FlowColumnOutputField'
  };
  return contentParams;
}


function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<FlowFieldsDisplayGridRowData, FieldsValidSortFields> {

  function getPropsFromRowData(rowData: FlowFieldsDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'flow-fields', content });
  }
  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<FlowFieldsDisplayGridRowData, FieldsValidSortFields>;
}
